/**
 * Error Boundary Component
 * Catches and displays errors in React component tree
 */

import { Box, Button, Container, Typography } from '@mui/material';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { errorReporting } from '../../services/errorReporting';

interface InnerProps {
  children: ReactNode;
  onReset: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryInner extends Component<InnerProps, State> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    errorReporting.report({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container data-testid="error-boundary">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              textAlign: 'center',
            }}
          >
            <Typography variant="h1" gutterBottom>
              Oops!
            </Typography>
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </Typography>
            <Button
              variant="contained"
              onClick={this.handleReset}
              data-testid="error-boundary-home-btn"
              sx={{ background: '#2B9BA5', '&:hover': { background: '#247f88' } }}
            >
              Go to Home
            </Button>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Use inside Router (route-level) — navigates with React Router, no page reload
export const ErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  return (
    <ErrorBoundaryInner onReset={() => navigate('/', { replace: true })}>
      {children}
    </ErrorBoundaryInner>
  );
};

// Use outside Router (App.tsx top-level) — falls back to window.location since no Router context exists
export const RootErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundaryInner onReset={() => window.location.replace('/')}>
    {children}
  </ErrorBoundaryInner>
);
